import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';
import { HHDSButtonResidenceClose, HHDSButtonResidenceCloseTagName } from '../ButtonResidenceClose/ButtonResidenceClose';
import gsap from 'gsap';

const DEBUG_VERBOSE: boolean = false;
export const HHDSResidenceCardTagName: string = 'hhds-residence-card';
const TAG_NAME: string = HHDSResidenceCardTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSResidenceCardAttrNames = {
	floorplanImgSrc: 'floorplan-img-src',
	floorplateImgSrc: 'floorplate-img-src',
	floorplanImgAlt: 'floorplan-img-alt',
	floorplateImgAlt: 'floorplate-img-alt',
	revealAnimation: 'reveal-animation',
};

const Attrs = HHDSResidenceCardAttrNames;

export class HHDSResidenceCard extends Component {
	private resizeFunc: any;

	constructor() {
		super();
		this.resizeFunc = () => {
			this.updateLayout();
		};
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const floorplanSrc = this.vars.get<string>(HHDSResidenceCardAttrNames.floorplanImgSrc);
		const floorplateSrc = this.vars.get<string>(HHDSResidenceCardAttrNames.floorplateImgSrc);
		const floorplanAlt = this.vars.get<string>(HHDSResidenceCardAttrNames.floorplanImgAlt);
		const floorplateAlt = this.vars.get<string>(HHDSResidenceCardAttrNames.floorplateImgAlt);

		const html: string = `
			<span slot="loc"></span>
			<div class="${TAG_NAME}">
			<div class="${TAG_NAME}__close"><${HHDSButtonResidenceCloseTagName}></ ${HHDSButtonResidenceCloseTagName}></div>
				<div class="${TAG_NAME}__main grid">
					<div class="${TAG_NAME}__sidebar col-span-4 md:col-span-10 md:col-start-2 lg:col-span-6 lg:col-start-1">
						<div class="${TAG_NAME}__sidebar-top">
							<div class="${TAG_NAME}__sidebar-top-text">
								<h5 class="heading heading--05"><slot name="eyebrow-text"></slot></h5>
								<h1 class="heading heading--01"><slot name="header"></slot></h1>
								<h5 class="heading heading--05"><slot name="plan-type"></h5>
							</div>
							<div class="${TAG_NAME}__sidebar__divider"></div>
							<p class="paragraph ${TAG_NAME}__sidebar__body-left"><slot name="body-left"></p>
							<p class="paragraph ${TAG_NAME}__sidebar__body-center"><slot name="body-center"></p>
						</div>
						<div class="${TAG_NAME}__sidebar-minimap-area">
							<img src="${floorplateSrc}" alt="${floorplateAlt}" class="${TAG_NAME}__sidebar__floorplate"></img>
						</div>
					</div>
					<div class="${TAG_NAME}__image-area col-span-4 md:col-span-10 md:col-start-2 lg:col-span-18 lg:col-start-7">
						<img src="${floorplanSrc}" alt="${floorplanAlt}"></img>
						<div class="${TAG_NAME}__disclaimer ${TAG_NAME}--disclaimer-inline"><p class="paragraph-sm"><slot name="approx-inline"></slot></p></div>
					</div>
					<div class="${TAG_NAME}__disclaimer ${TAG_NAME}--disclaimer-standalone"><p class="paragraph-sm"><slot name="approx-standalone"></slot></p></div>
					<div class="${TAG_NAME}__standalone-minimap-area sm:col-span-4 col-span-4 md:col-span-4 md:col-start-5">
						<img src="${floorplateSrc}" alt="${floorplateAlt}" class="${TAG_NAME}__sidebar__floorplate"></img>
					</div>
				</div>
			</div>
    `;

		this.setInnerHtml('', '', html);

		const imageArea = this.element<HTMLDivElement>(`.${TAG_NAME}__image-area`);
		const image = imageArea.querySelector('img');
		imageArea.style.display = 'none';
		image?.addEventListener('load', () => this.updateLayout());
		window.addEventListener('resize', this.resizeFunc);

		if (this.vars.get<boolean>(Attrs.revealAnimation)) {
			requestAnimationFrame(() => {
				this.animateElementsIn();
			});
		}

		this.observeSlotChanges(true);
		this.observeBreakpointChanges(() => {});
	}

	private animateElementsIn(): void {
		const eyebrow = this.element('[slot="eyebrow-text"]');
		const header = this.element('[slot="header"]');
		const planType = this.element('[slot="plan-type"]');
		const divider = this.element<HTMLDivElement>(`.${TAG_NAME}__sidebar__divider`);
		const bodyLeft = this.element<HTMLParagraphElement>(`.${TAG_NAME}__sidebar__body-left`);
		const bodyCenter = this.element<HTMLParagraphElement>(`.${TAG_NAME}__sidebar__body-center`);

		const minimap = this.element<HTMLDivElement>(`.${TAG_NAME}__sidebar-minimap-area`);
		const image = this.element<HTMLImageElement>(`.${TAG_NAME}__image-area`);
		const standaloneMinimap = this.element<HTMLDivElement>(`.${TAG_NAME}__standalone-minimap-area`);

		const disclaimerInline = this.element<HTMLDivElement>(`.${TAG_NAME}--disclaimer-inline`);
		const disclaimerStandalone = this.element<HTMLDivElement>(`.${TAG_NAME}--disclaimer-standalone`);

		let startTime: number = 0.1;
		if (eyebrow) {
			gsap.from(eyebrow, { opacity: 0, x: 40, delay: startTime, duration: 0.8, ease: 'power2.out' });
			startTime += 0.2;
		}
		if (header) {
			gsap.from(header, { opacity: 0, x: 40, delay: startTime, duration: 0.8, ease: 'power2.out' });
			startTime += 0.2;
		}
		if (planType) {
			gsap.from(planType, { opacity: 0, x: 40, delay: startTime, duration: 0.5, ease: 'power2.out' });
			startTime += 0.2;
		}

		if (divider) {
			gsap.from(divider, { opacity: 0, width: '0px', delay: startTime, duration: 0.5, ease: 'power2.out' });
			startTime += 0.2;
		}
		if (bodyLeft && bodyLeft.style.display !== 'none') {
			gsap.from(bodyLeft, { opacity: 0, x: -10, delay: startTime, duration: 0.5, ease: 'power2.out' });
		}
		if (bodyCenter && bodyCenter.style.display !== 'none') {
			gsap.from(bodyCenter, { opacity: 0, y: -10, delay: startTime, duration: 0.5, ease: 'power2.out' });
		}
		if (bodyLeft || bodyCenter) {
			startTime += 0.2; // add a small delay before starting the next animations
		}

		startTime += 0.1; // add a small delay before starting the next animations

		if (image) {
			gsap.from(image, { opacity: 0, transform: 'scale(0.97)', delay: startTime, duration: 0.7, ease: 'power2.out' });
			gsap.from(disclaimerInline, { opacity: 0, y: 0, delay: startTime, duration: 0.5, ease: 'power2.out' });
			gsap.from(disclaimerStandalone, { opacity: 0, y: 0, delay: startTime, duration: 0.5, ease: 'power2.out' });
			startTime += 0.3;
		}

		if (minimap && minimap.style.display !== 'none') {
			gsap.from(minimap, { opacity: 0, y: -20, delay: startTime, duration: 0.5, ease: 'power2.out' });
		}
		if (standaloneMinimap && standaloneMinimap.style.display !== 'none') {
			gsap.from(standaloneMinimap, { opacity: 0, y: -20, delay: startTime, duration: 0.5, ease: 'power2.out' });
		}
	}

	private updateLayout(): void {
		const imageArea = this.element<HTMLDivElement>(`.${TAG_NAME}__image-area`);
		imageArea.style.display = 'flex';
		const image = imageArea.querySelector('img');
		if (this.currentBreakpoint == 'lg' || this.currentBreakpoint == 'xl') {
			// check whether the image is taller than its container
			const imageMargin = getComputedStyle(image!).marginTop;
			const imageHeight = image!.clientHeight + parseFloat(imageMargin) * 2; // include margin
			const containerHeight = imageArea.clientHeight;
			const ratio = imageHeight / containerHeight;
			if (ratio >= 1) {
				imageArea.style.justifyContent = 'flex-start';
			} else {
				imageArea.style.justifyContent = 'center';
			}
		} else {
			imageArea.style.justifyContent = 'center';
		}

		/*if (this.currentBreakpoint == 'xs' || this.currentBreakpoint == 'sm' || this.currentBreakpoint == 'md') {
			const mainEl = this.element<HTMLDivElement>(`.${TAG_NAME}__main`);
			// get the mainEl's position with respect to the viewport
			const mainRect = mainEl.getBoundingClientRect();
			const mainPadding = mainRect.top * 2;
			const componentEl = this.element<HTMLDivElement>(`.${TAG_NAME}`);
			const availableHeight = window.innerHeight - mainPadding;
			const ratio = mainEl.offsetHeight / availableHeight;
			console.log(availableHeight, componentEl.clientHeight, ratio);
			if (ratio > 1) {
				componentEl.style.height = 'unset';
				console.log('Height unset');
			} else {
				componentEl.style.height = '100%';
				console.log('Height 100%');
			}
		}*/
	}

	get closeButton(): HHDSButtonResidenceClose | null {
		return this.element<HHDSButtonResidenceClose>(HHDSButtonResidenceCloseTagName);
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		window.removeEventListener('resize', this.resizeFunc);
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.floorplanImgSrc]: {
		description: 'The source URL for the floorplan image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.floorplateImgSrc]: {
		description: 'The source URL for the floorplate image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.floorplanImgAlt]: {
		description: 'The alt text for the floorplan image.',
		defaultValue: 'Floorplan',
		type: String,
	},
	[Attrs.floorplateImgAlt]: {
		description: 'The alt text for the floorplate image.',
		defaultValue: 'Floorplate',
		type: String,
	},
	[Attrs.revealAnimation]: {
		description: 'Whether to animate elements of the card when shown.',
		type: Boolean,
		defaultValue: true,
	},
};
